import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './CasosPage.module.css';

import { getAllCasesByStatus, searchCases, deleteCase, sendSummaryEmail } from '../../services/clienteService';
import { ClienteCaso, EstadoCaso } from '../../interfaces/cliente.types';

import { CasoTable } from './components/CasoTable';
import { AddCasoModal } from './components/AddCasoModal';
import { EditCasoModal } from './components/EditCasoModal';
import { DeleteCasoModal } from './components/DeleteCasoModal';
import { CasoDetailModal } from './components/CasoDetailModal'; // <-- ¡SE IMPORTA EL NUEVO DOSSIER!

import { Search, Plus, Loader, AlertTriangle } from 'lucide-react';
import { ChangeStatusModal } from './components/cardinfo/modales/ChangeStatusModal';
export const CasosPage: React.FC = () => {
    const [casos, setCasos] = useState<ClienteCaso[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [activeFilter, setActiveFilter] = useState<EstadoCaso>('Activo');
    const [searchTerm, setSearchTerm] = useState('');
    
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [caseToEdit, setCaseToEdit] = useState<ClienteCaso | null>(null);
    const [caseToDelete, setCaseToDelete] = useState<ClienteCaso | null>(null);
    const [viewingCaseId, setViewingCaseId] = useState<number | null>(null);
    const [sendingEmailId, setSendingEmailId] = useState<number | null>(null);
    const [caseToChangeStatus, setCaseToChangeStatus] = useState<ClienteCaso | null>(null);

    const fetchCasos = useCallback(async (estado: EstadoCaso) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await getAllCasesByStatus(estado);
            setCasos(data);
        } catch (err: any) {
            setError(err.message || 'No se pudo cargar la lista de casos.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (searchTerm === '') {
            fetchCasos(activeFilter);
        }
    }, [activeFilter, searchTerm, fetchCasos]);

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (searchTerm.length > 0 && searchTerm.length < 3) {
            setError("El término de búsqueda debe tener al menos 3 caracteres.");
            return;
        }
        if (searchTerm === '') {
            fetchCasos(activeFilter);
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const data = await searchCases(searchTerm);
            setCasos(data);
        } catch (err: any) {
            setError(err.message || 'Error en la búsqueda.');
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleCaseCreated = (newCase: ClienteCaso) => {
        if(newCase.estado_caso === activeFilter) {
            setCasos(prev => [newCase, ...prev]);
        }
        setIsAddModalOpen(false);
    };

    const handleCaseUpdated = (updatedCase: ClienteCaso) => {
        setCasos(prev => prev.map(c => c.id === updatedCase.id ? updatedCase : c));
        setCaseToEdit(null);
    };
    
    const handleCaseDeleted = async (caseId: number) => {
        try {
            await deleteCase(caseId);
            setCasos(prev => prev.filter(c => c.id !== caseId));
            setCaseToDelete(null);
        } catch (err: any) {
            setError(err.message);
        }
    };
 const handleStatusUpdated = () => {
        fetchCasos(activeFilter);
    };
    const handleFilterChange = (estado: EstadoCaso) => {
        setSearchTerm('');
        setActiveFilter(estado);
    };
  const handleSendSummary = async (caseId: number) => {
        setSendingEmailId(caseId);
        try {
            const message = await sendSummaryEmail(caseId);
            alert(message);
        } catch (err: any) {
            alert(`Error al enviar el correo: ${err.message}`);
        } finally {
            setSendingEmailId(null);
        }
    };
     const renderContent = () => {
        if (isLoading) {
            return <div className={styles.stateMessage}><Loader className={styles.spinner} />Cargando casos...</div>;
        }
        if (error) {
            return <div className={`${styles.stateMessage} ${styles.errorState}`}><AlertTriangle />{error}</div>;
        }
        return <CasoTable 
            casos={casos} 
            onEdit={setCaseToEdit} 
            onDelete={setCaseToDelete} 
            onViewDetails={setViewingCaseId}
            onSendEmail={handleSendSummary}
            sendingEmailId={sendingEmailId}
              onStatusClick={setCaseToChangeStatus}
        />;
    };


    return (
        <>
            <div className={styles.casosPageContainer}>
                <header className={styles.header}>
                    <div>
                        <h1 className={styles.title}>Gestión de Expedientes</h1>
                        <p className={styles.subtitle}>Administra, busca y organiza todos tus casos legales.</p>
                    </div>
                    <button className={styles.createCaseButton} onClick={() => setIsAddModalOpen(true)}>
                        <Plus size={20} />
                        <span>Registrar Nuevo Caso</span>
                    </button>
                </header>

                <div className={styles.controlsContainer}>
                    <div className={styles.filterTabs}>
                        {(['Activo', 'En Archivo', 'Cerrado', 'Consulta'] as EstadoCaso[]).map(estado => (
                            <button 
                                key={estado}
                                className={`${styles.filterTab} ${activeFilter === estado ? styles.activeFilter : ''}`}
                                onClick={() => handleFilterChange(estado)}
                            >
                                {estado}
                            </button>
                        ))}
                    </div>
                    <form onSubmit={handleSearch} className={styles.searchBar}>
                        <Search size={18} className={styles.searchIcon} />
                        <input 
                            type="text" 
                            placeholder="Buscar por cliente, DNI o expediente..." 
                            className={styles.searchInput}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </form>
                </div>

                <main className={styles.content}>
                    {renderContent()}
                </main>
            </div>

            <AnimatePresence>
                {isAddModalOpen && <AddCasoModal onClose={() => setIsAddModalOpen(false)} onCaseCreated={handleCaseCreated} />}
                {caseToEdit && <EditCasoModal onClose={() => setCaseToEdit(null)} onCaseUpdated={handleCaseUpdated} caseToEdit={caseToEdit} />}
                {caseToDelete && <DeleteCasoModal onClose={() => setCaseToDelete(null)} onConfirmDelete={handleCaseDeleted} caseToDelete={caseToDelete} />}
                {viewingCaseId && <CasoDetailModal caseId={viewingCaseId} onClose={() => setViewingCaseId(null)} />}
                     {caseToChangeStatus && <ChangeStatusModal caso={caseToChangeStatus} onClose={() => setCaseToChangeStatus(null)} onStatusUpdated={handleStatusUpdated} />}
            </AnimatePresence>
        </>
    );
};